import React, { useState, useEffect } from 'react';
import { FaPlay, FaPause, FaForward, FaBackward, FaRedo } from 'react-icons/fa';
import './Dashboard.css';
import DonutChart from '../../components/Charts/DonutChart';
import LineChart from '../../components/Charts/LineChart';
import TreeView from '../../components/TreeView/TreeView';
import useStatusAnimation from '../../hooks/useStatusAnimation';
import { generateAssetData, generateHistoricalData, ASSETS, STATUS_TYPES } from '../../utils/dataGenerator';

const Dashboard = () => {
  const {
    currentStatus,
    isPlaying,
    cycleCount,
    togglePlayPause,
    nextStatus,
    previousStatus,
    reset
  } = useStatusAnimation(true);

  const [assetData, setAssetData] = useState({});
  const [historicalData, setHistoricalData] = useState({});

  // Update Daten wenn Status sich ändert
  useEffect(() => {
    const newData = generateAssetData(currentStatus);
    setAssetData(newData);

    // Generiere historische Daten für Charts
    const historical = {};
    Object.keys(ASSETS).forEach(assetKey => {
      const asset = ASSETS[assetKey];
      const firstSensor = Object.keys(asset.sensors)[0];
      historical[asset.id] = generateHistoricalData(
        asset.sensors[firstSensor],
        currentStatus,
        15
      );
    });
    setHistoricalData(historical);
  }, [currentStatus]);

  const getStatusColor = (status) => {
    switch (status) {
      case STATUS_TYPES.NORMAL:
        return '#00C851';
      case STATUS_TYPES.WARNING:
        return '#FF8800';
      case STATUS_TYPES.CRITICAL:
        return '#FF4444';
      default:
        return '#B0B0B0';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case STATUS_TYPES.NORMAL:
        return 'Normalbetrieb';
      case STATUS_TYPES.WARNING:
        return 'Warnung';
      case STATUS_TYPES.CRITICAL:
        return 'Kritisch';
      default:
        return 'Unbekannt';
    }
  };

  const getDonutData = (status) => {
    switch (status) {
      case STATUS_TYPES.NORMAL:
        return [90, 8, 2];
      case STATUS_TYPES.WARNING:
        return [60, 35, 5];
      case STATUS_TYPES.CRITICAL:
        return [20, 30, 50];
      default:
        return [33, 33, 34];
    }
  };

  return (
    <div className="dashboard">
      {/* Status Header */}
      <div className="status-header" style={{ borderLeft: `4px solid ${getStatusColor(currentStatus)}` }}>
        <div className="status-info">
          <h2 className="status-title">Systemstatus</h2>
          <div className="status-badge" style={{ backgroundColor: `${getStatusColor(currentStatus)}33`, color: getStatusColor(currentStatus) }}>
            <span className="status-dot" style={{ backgroundColor: getStatusColor(currentStatus) }}></span>
            {getStatusLabel(currentStatus)}
          </div>
        </div>
        
        {/* Animation Controls */}
        <div className="animation-controls">
          <button onClick={previousStatus} title="Vorheriger Status" className="control-btn">
            <FaBackward />
          </button>
          <button onClick={togglePlayPause} title={isPlaying ? 'Pause' : 'Play'} className="control-btn primary">
            {isPlaying ? <FaPause /> : <FaPlay />}
          </button>
          <button onClick={nextStatus} title="Nächster Status" className="control-btn">
            <FaForward />
          </button>
          <button onClick={reset} title="Zurücksetzen" className="control-btn">
            <FaRedo />
          </button>
          <div className="cycle-counter">
            Zyklus: {cycleCount}
          </div>
        </div>
      </div>

      {/* Hauptstatus-Anzeige */}
      <div className="main-status-card">
        <div className="status-grid">
          <DonutChart 
            title="Gesamtübersicht" 
            data={getDonutData(currentStatus)}
            status={currentStatus}
          />
          <div className="status-messages">
            <h3>Aktuelle Meldungen</h3>
            {assetData[ASSETS.PRODUKTIONSLINIE.id]?.messages?.map((msg, idx) => (
              <div key={idx} className="message-item" style={{ borderLeftColor: getStatusColor(currentStatus) }}>
                <div className="message-text">{msg.text}</div>
                {msg.prediction && (
                  <div className="message-prediction">
                    <span className="prediction-icon">📅</span>
                    <span className="prediction-label">Ausfall berechnet am:</span>
                    <span className="prediction-date">
                      {new Date(msg.prediction.date).toLocaleDateString('de-DE', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric' 
                      })}
                    </span>
                    <span className="prediction-prob" style={{ 
                      color: msg.prediction.probability >= 80 ? '#FF4444' : '#FF8800' 
                    }}>
                      ({msg.prediction.probability}%)
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
          <TreeView assetData={assetData} currentStatus={currentStatus} />
        </div>
      </div>

      {/* Anlagen-Karten */}
      <div className="assets-grid">
        {Object.keys(ASSETS).map(assetKey => {
          const asset = ASSETS[assetKey];
          const data = assetData[asset.id];
          
          if (!data) return null;

          return (
            <div key={asset.id} className="asset-card">
              <div className="asset-header">
                <h3>{asset.name}</h3>
                <span 
                  className="asset-status-badge"
                  style={{ 
                    backgroundColor: `${getStatusColor(data.status)}33`,
                    color: getStatusColor(data.status)
                  }}
                >
                  {getStatusLabel(data.status)}
                </span>
              </div>

              {/* Sensor-Werte */}
              <div className="sensor-grid">
                {Object.keys(data.readings).slice(0, 4).map(sensorKey => {
                  const reading = data.readings[sensorKey];
                  return (
                    <div key={sensorKey} className="sensor-value">
                      <div className="sensor-label">{sensorKey}</div>
                      <div className="sensor-reading">
                        {reading.value} <span className="unit">{reading.unit}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Chart */}
              {historicalData[asset.id] && (
                <LineChart
                  title="Verlauf (15min)"
                  data={historicalData[asset.id].map(d => d.value)}
                  labels={historicalData[asset.id].map((_, i) => `-${15-i}m`)}
                  color={getStatusColor(currentStatus)}
                  unit={Object.values(data.readings)[0]?.unit}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Dashboard;
